import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { createThirdwebClient, defineChain, getContract } from "thirdweb";
import {
  orderProcessorAbi as ORDER_PROCESSOR_ARTIFACT,
  erc20Abi as ERC20_ARTIFACT,
  merchantRegistryAbi as MERCHANT_REGISTRY_ARTIFACT,
} from "@/abi.ts";
import {
  AutoConnectProps,
  darkTheme,
  useActiveAccount,
  useActiveWallet,
  useAutoConnect,
  useConnectModal,
  UseConnectModalOptions,
  useWalletDetailsModal,
  UseWalletDetailsModalOptions,
} from "thirdweb/react";
import EthCrypto from "eth-crypto";
import { LocalStorageKeys } from "@/constants";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useMerchant } from "./Merchant";
import { walletDetailsModalConfig, currentChain } from "./chain";
import { inAppWallet } from "thirdweb/wallets";
import { __setUser } from "@/sentry";

type SmartWalletContextType = {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  smartWalletAddress: string | null;
  isLoggedIn: boolean;
  account: any;
  isConnecting: boolean;
  openDetailsModal: () => void;
};

const initialWalletContext: SmartWalletContextType = {
  login: async () => undefined,
  logout: async () => undefined,
  smartWalletAddress: null,
  isLoggedIn: false,
  account: null,
  isConnecting: false,
  openDetailsModal: () => undefined,
};

const SmartWalletContext = createContext(initialWalletContext);

export const client = createThirdwebClient({
  clientId: import.meta.env.VITE_THIRDWEB_CLIENT_ID,
});

export const chain = defineChain({
  id: currentChain.id,
  rpc: import.meta.env.VITE_RPC_URL as any,
  nativeCurrency: currentChain.nativeCurrency,
  testnet: currentChain?.testnet,
  blockExplorers: currentChain.blockExplorers,
});

export const usdtContract = getContract({
  client,
  chain,
  address: import.meta.env.VITE_USDT_CONTRACT_ADDRESS,
  abi: ERC20_ARTIFACT as readonly any[],
});

export const orderProcessorContract = getContract({
  client,
  chain,
  address: import.meta.env.VITE_ORDER_PROCESSOR,
  abi: ORDER_PROCESSOR_ARTIFACT as readonly any[],
});

export const merchantRegistryContract = getContract({
  client,
  chain,
  address: import.meta.env.VITE_MERCHANT_REGISTRY,
  abi: MERCHANT_REGISTRY_ARTIFACT as readonly any[],
});

export const SmartWalletProvider = ({ children }: { children: ReactNode }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { connect } = useConnectModal();
  const wallet = useActiveWallet();
  const account = useActiveAccount();
  const walletConfig: AutoConnectProps | UseConnectModalOptions = {
    chain,
    client,
    onTimeout: () => {
      toast({
        title: "Error when logging in",
        description:
          "Login timed out. Please check your network and try again.",
        variant: "destructive",
      });
    },
    size: "compact",
    accountAbstraction: {
      sponsorGas: true,
      chain,
      factoryAddress: import.meta.env.VITE_AA_FACTORY_ADDRESS,
    },
    theme: darkTheme({
      colors: {
        accentText: "#62C1EF",
        accentButtonBg: "#01c38e",
        secondaryButtonText: "#ededef",
        accentButtonText: "#131418",
      },
    }),
    wallets: [
      inAppWallet({
        auth: {
          options: ["google", "email", "phone", "passkey"],
          defaultSmsCountryCode: "IN",
        },
      }),
    ],
    showAllWallets: false,
    titleIcon:
      "https://firebasestorage.googleapis.com/v0/b/p2px-421205.appspot.com/o/logo_dark.png?alt=media&token=63ab6394-823c-4272-b79c-f160b33c7ef3",
    welcomeScreen: {
      title: "P2P.me - Fully Decentralised P2P Exchange",
      subtitle: "Decentralized Trading, Central to Trust!",
      img: {
        src: "https://firebasestorage.googleapis.com/v0/b/p2px-421205.appspot.com/o/logo_dark.png?alt=media&token=63ab6394-823c-4272-b79c-f160b33c7ef3",
        width: 150,
        height: 150,
      },
    },
    showThirdwebBranding: false,
  };

  const [smartWalletAddress, setSmartWalletAddress] = useState<string | null>(
    null,
  );
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const {
    merchantDetails,
    isLoading: merchantLoading,
    hasFetched: merchantFetched,
  } = useMerchant();

  useEffect(() => {
    if (merchantDetails && merchantFetched && account?.address) {
      console.log("merchantDetails-sm", merchantDetails);
      localStorage.setItem(
        LocalStorageKeys.MERCHANT_DETAILS,
        JSON.stringify(merchantDetails),
      );
    }
  }, [merchantDetails, merchantFetched, merchantLoading, account]);

  const { isLoading } = useAutoConnect(walletConfig);
  useEffect(() => {
    if (account?.address) {
      __setUser(account.address);
      setIsLoggedIn(true);

      localStorage.setItem(LocalStorageKeys.LOGGED_IN, String(true));

      // Retrieve or create the relay identity
      let relayIdentity = localStorage.getItem(LocalStorageKeys.RELAY_IDENTITY);

      if (!relayIdentity) {
        console.log("Creating new relay identity");
        relayIdentity = JSON.stringify(EthCrypto.createIdentity());
        localStorage.setItem(LocalStorageKeys.RELAY_IDENTITY, relayIdentity);
      } else {
        relayIdentity = JSON.parse(relayIdentity);
      }

      setSmartWalletAddress(account?.address);
    } else {
      localStorage.removeItem(LocalStorageKeys.LOGGED_IN);
      localStorage.removeItem(LocalStorageKeys.MERCHANT_DETAILS);
    }
  }, [account]);

  const detailsModal = useWalletDetailsModal();

  const openDetailsModal = () => {
    detailsModal.open({
      ...walletDetailsModalConfig,
      client,
    } as UseWalletDetailsModalOptions);
  };

  const login = async () => {
    try {
      await connect(walletConfig);
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Error when logging in",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const logout = async () => {
    try {
      await wallet?.disconnect();
      setIsLoggedIn(false);
      localStorage.removeItem(LocalStorageKeys.RELAY_IDENTITY);
      localStorage.removeItem(LocalStorageKeys.CONNECTED_WALLET_IDS);
      localStorage.removeItem(LocalStorageKeys.MERCHANT_UPI);
      navigate(0);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <SmartWalletContext.Provider
      value={{
        login,
        logout,
        smartWalletAddress,
        isLoggedIn,
        account,
        isConnecting: isLoading,
        openDetailsModal,
      }}>
      {children}
    </SmartWalletContext.Provider>
  );
};

export const useSmartWallet = () => useContext(SmartWalletContext);
